import { CalendarDays } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  formatIsoDate,
  formatLongDate,
  MONTH_LABELS,
  parseIsoDate,
  startOfDay,
} from "@/lib/calendarDates";
import { useDismissable } from "@/lib/useDismissable";
import { CalendarGrid, type DayVariant } from "./CalendarGrid";

const POPOVER_GAP = 4;

function scrollParentOf(node: HTMLElement | null): HTMLElement | null {
  let current = node?.parentElement ?? null;

  while (current) {
    const { overflowY } = getComputedStyle(current);

    if (overflowY === "auto" || overflowY === "scroll") {
      return current;
    }

    current = current.parentElement;
  }

  return null;
}

export function DatePicker({
  value,
  onChange,
  minDate,
  maxDate,
  label,
  id,
}: {
  value: string;
  onChange: (iso: string) => void;
  minDate?: Date;
  maxDate?: Date;
  label: string;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [opensUp, setOpensUp] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const ref = useDismissable<HTMLDivElement>(open, (reason) => {
    setOpen(false);

    if (reason === "escape") {
      triggerRef.current?.focus();
    }
  });

  const today = useMemo(() => startOfDay(new Date()), []);
  const selected = useMemo(() => parseIsoDate(value), [value]);
  const [activeDate, setActiveDate] = useState(() => selected ?? today);

  useEffect(() => {
    if (open) {
      // oxlint-disable-next-line react/set-state-in-effect
      setActiveDate(selected ?? today);
    }
  }, [open, selected, today]);

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const popover = popoverRef.current;

    if (!open || !trigger || !popover) {
      return;
    }

    const anchor = trigger.getBoundingClientRect();
    const height = popover.offsetHeight;
    const container = scrollParentOf(trigger);
    const bounds = container
      ? container.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight };

    setOpensUp(
      anchor.bottom + POPOVER_GAP + height > bounds.bottom &&
        anchor.top - POPOVER_GAP - height > bounds.top,
    );
  }, [open]);

  function variantOf(iso: string): DayVariant {
    return iso === value ? "selected" : "none";
  }

  return (
    <div ref={ref} className="relative">
      <button
        id={id}
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex h-10 w-full items-center gap-2 rounded-lg border border-muted bg-surface px-3 text-left text-sm text-foreground focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
      >
        <CalendarDays
          className="h-4 w-4 shrink-0 text-foreground"
          strokeWidth={2}
        />
        <span>{selected ? formatLongDate(selected) : "Choose a date"}</span>
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={label}
          ref={popoverRef}
          className={`absolute left-0 z-10 w-64 rounded-lg border border-muted bg-surface p-3 shadow-2xl ${
            opensUp ? "bottom-full mb-1" : "top-full mt-1"
          }`}
        >
          <CalendarGrid
            activeDate={activeDate}
            onActiveDateChange={setActiveDate}
            onSelect={(day) => {
              onChange(formatIsoDate(day));
              setOpen(false);
            }}
            variantOf={variantOf}
            minDate={minDate}
            maxDate={maxDate}
            label={`${MONTH_LABELS[activeDate.getMonth()]} ${activeDate.getFullYear()}`}
          />
        </div>
      )}
    </div>
  );
}
